import { createFileRoute } from '@tanstack/react-router';
import { Phone } from 'lucide-react';
import { Badge } from '@/components/atoms/badge';
import { Skeleton } from '@/components/atoms/skeleton';
import { DetailPageLayout } from '@/components/layouts';
import { Fullbleed } from '@/contexts/fullbleed';
import { usePhoneNumbers } from '@/lib/messaging/hooks';
import { formatPhoneDisplay } from '@/lib/contacts/phone';

export const Route = createFileRoute('/_dashboard/dashboard/settings/phone')({
  component: PhoneSettingsPage,
});

function PhoneSettingsPage() {
  // Source of truth is the messaging module's hook — same one the inbox
  // / phone-numbers admin already use. The operations row shape
  // (e164/purpose/status/provider) flows through unchanged so the badges
  // below show real metadata rather than fabricated `local`/`active`
  // values.
  const { data, isLoading } = usePhoneNumbers();
  const numbers = data?.data ?? [];

  return (
    <>
      <Fullbleed />
      <DetailPageLayout
        header={
          <div className="min-w-0">
            <h1 className="truncate text-base font-semibold">Phone</h1>
            <p className="text-xs text-muted-foreground">
              Provisioned phone numbers for your account.
            </p>
          </div>
        }
        contentClassName="px-4 py-4 md:px-5 md:py-5"
      >
        {isLoading ? (
          <div className="max-w-2xl space-y-4">
            <Skeleton className="h-20 w-full rounded-xl" />
            <Skeleton className="h-32 w-full rounded-xl" />
          </div>
        ) : (
          <div className="max-w-2xl space-y-5">
            <div className="overflow-hidden rounded-xl border">
              <div className="border-b bg-muted/30 px-4 py-3">
                <p className="text-sm font-medium">Phone numbers</p>
                <p className="text-xs text-muted-foreground">
                  Provisioned numbers for your account.
                </p>
              </div>

              {numbers.length === 0 ? (
                <div className="flex items-center gap-3 px-4 py-6 text-sm text-muted-foreground">
                  <Phone className="h-4 w-4 shrink-0" />
                  No phone numbers provisioned yet.
                </div>
              ) : (
                <ul className="divide-y">
                  {numbers.map((num) => (
                    <li key={num.id} className="flex items-center justify-between px-4 py-3">
                      <div className="flex items-center gap-3">
                        <Phone className="h-4 w-4 shrink-0 text-muted-foreground" />
                        <div className="min-w-0">
                          <p className="text-sm font-medium">{formatPhoneDisplay(num.e164)}</p>
                          {num.label ? (
                            <p className="text-xs text-muted-foreground">{num.label}</p>
                          ) : null}
                        </div>
                      </div>
                      <div className="flex shrink-0 items-center gap-2">
                        <Badge variant="outline" className="capitalize text-xs">
                          {num.purpose.replace(/_/g, ' ').toLowerCase()}
                        </Badge>
                        <Badge
                          variant={num.status === 'ACTIVE' ? 'default' : 'secondary'}
                          className="capitalize text-xs"
                        >
                          {num.status.toLowerCase()}
                        </Badge>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}
      </DetailPageLayout>
    </>
  );
}
